import clsx from 'clsx';
import { X } from 'react-feather';
import { useForm } from 'react-hook-form';
import Modal from 'react-modal';
import { useDispatch } from 'react-redux';
import { formatNumber } from '../services/helpers';
import { isValidSpanishPostalCode } from '../services/validators';
import { setRecipientDetails as posSetRecipientDetails } from '../store/pos-slice';
import { setRecipientDetails as repairSetRecipientDetails } from '../store/repair-slice';

interface Props {
  onClose: () => void;
  onSave: () => void;
  open: boolean;
  type: 'repair' | 'sale';
}

interface FormValues {
  customerLegalName: string;
  customerTaxNumber: string;
  customerAddress: string;
  customerPostalCode: string;
}

const RecipientModal = ({ onClose, onSave, open, type }: Props) => {
  const dispatch = useDispatch();
  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm<FormValues>({
    mode: 'onSubmit',
    defaultValues: {
      customerLegalName: '',
      customerTaxNumber: '',
      customerAddress: '',
      customerPostalCode: '',
    },
  });

  const close = () => {
    onClose();
    reset();
  };

  const onSubmit = async (formValues: FormValues) => {
    if (type === 'repair') {
      dispatch(repairSetRecipientDetails(formValues));
    } else {
      dispatch(posSetRecipientDetails(formValues));
    }
    onSave();
    reset();
  };

  return (
    <Modal className="vercomi-modal my-form notes-modal" isOpen={open} onRequestClose={() => close()} shouldCloseOnOverlayClick={false}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="content">
          <div className="d-flex justify-content-between mb-3">
            <div className="title">Datos del cliente para emisión de factura</div>
            <button type="button" className="close-button-modal" onClick={() => close()} title="Cerrar">
              <X size={16} />
            </button>
          </div>
          <div className="row my-3">
            <div className="col">
              <p>
                Según la normativa vigente de la Agencia Tributaria, para compras superiores a {formatNumber(parseFloat(process.env.REACT_APP_MAX_SALE_AMOUNT!))} € es obligatorio emitir una factura
                nominativa.
              </p>
              <p>Por este motivo, necesitamos que nos facilites los datos de facturación antes de completar la operación.</p>
              <p>Esta información se utiliza únicamente para la emisión y conservación de la factura conforme a las obligaciones fiscales establecidas por la ley.</p>
            </div>
          </div>
          <div className="row my-3">
            <div className="col-12">
              <div className={clsx('input-name', { error: errors?.customerLegalName })}>Nombre del cliente o razón social *</div>
              <input
                type="text"
                {...register('customerLegalName', {
                  required: true,
                  setValueAs: (v) => v.trim(),
                })}
                className={clsx({ error: errors?.customerLegalName })}
                placeholder="Ejemplo: Enrique García Martínez o Joyería García Martínez SL"
              />
              {errors.customerLegalName && <div className="error-message">{errors.customerLegalName.message}</div>}
            </div>
          </div>
          <div className="row my-3">
            <div className="col-12">
              <div className={clsx('input-name', { error: errors?.customerTaxNumber })}>NIF o CIF *</div>
              <input
                type="text"
                {...register('customerTaxNumber', {
                  required: true,
                  setValueAs: (v) => v.trim(),
                })}
                className={clsx({ error: errors?.customerTaxNumber })}
                placeholder="Ejemplo: 12345678A o J12345678"
              />
              {errors.customerTaxNumber && <div className="error-message">{errors.customerTaxNumber.message}</div>}
            </div>
          </div>
          <div className="row my-3">
            <div className="col-12">
              <div className={clsx('input-name', { error: errors?.customerAddress })}>Dirección de facturación *</div>
              <input
                type="text"
                {...register('customerAddress', {
                  required: true,
                  setValueAs: (v) => v.trim(),
                })}
                className={clsx({ error: errors?.customerAddress })}
                placeholder="Ejemplo: Calle Alcalá 45 Madrid, Madrid"
              />
              <small style={{ fontSize: 10 }}>Formato: Calle número localidad, provincia</small>
              {errors.customerAddress && <div className="error-message">{errors.customerAddress.message}</div>}
            </div>
          </div>
          <div className="row my-3">
            <div className="col-12">
              <div className={clsx('input-name', { error: errors?.customerPostalCode })}>Código postal *</div>
              <input
                type="text"
                {...register('customerPostalCode', {
                  required: true,
                  setValueAs: (v) => v.trim(),
                  validate: (value) => {
                    if (!isValidSpanishPostalCode(value)) {
                      return 'Código postal inválido (formato ES).';
                    }
                    return true;
                  },
                })}
                className={clsx({ error: errors?.customerPostalCode })}
                placeholder="Ejemplo: 28014"
              />
              {errors.customerPostalCode && <div className="error-message">{errors.customerPostalCode.message}</div>}
            </div>
          </div>
        </div>
        <button className={`save-button`} type="submit">
          Guardar
        </button>
      </form>
    </Modal>
  );
};

export default RecipientModal;
